import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    UpdateOrganisationDocument,
    IOrganisationFragment,
    OrganisationDocument,
} from 'graphql/types';
import { ApolloError } from 'common/components/ApolloError';
import { CoverPage } from 'hydra/pages/CoverPage';
import { PageTitle } from 'common/components/PageTitle';
import { Typography } from 'common/components/Typography';
import { Text } from 'common/components/Text';
import { OrganisationImagesForm } from 'organisation/components/forms/OrganisationImagesForm';
import { getUrl } from 'route/utils/getUrl';
import { OrganisationOnboardingLottie } from 'organisation/components/OrganisationOnboardingLottie';
import { OrganisationOnboardingNavigation } from 'organisation/components/OrganisationOnboardingNavigation';
import { getImageId } from 'common/utils/image';

export const OrganisationImagesPage = () => {
    const [translate] = useTranslation();

    const [overrideOrganisation, setOverrideOrganisation] = useState<
        Partial<IOrganisationFragment> | undefined
    >();

    const { data: currentOrgData } = useQuery(OrganisationDocument);
    const organisation = currentOrgData?.organisation;
    const { logo, participantIntroImage } = organisation || {};
    const [updateOrganisation, { error: updateError }] = useMutation(
        UpdateOrganisationDocument
    );

    return (
        <>
            {updateError && <ApolloError error={updateError} />}
            <CoverPage
                footer={<OrganisationOnboardingNavigation activeStep={1} />}
                secondaryContent={
                    <OrganisationOnboardingLottie
                        overrideOrganisation={overrideOrganisation}
                    />
                }
            >
                <PageTitle>{translate('orgImagesPage.browserTitle')}</PageTitle>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="h2">
                        {translate('orgImagesPage.title')}
                    </Typography>
                </Box>

                <Text>{translate('orgImagesPage.content')}</Text>

                <OrganisationImagesForm
                    initialValues={{
                        logo,
                        participantIntroImage,
                    }}
                    onChange={(name, file) => {
                        // Add timeout here so we wait for the slider to be finished with the animation
                        setTimeout(() => {
                            setOverrideOrganisation((org) => ({
                                ...org,
                                [name]: file,
                            }));
                        }, 250);
                    }}
                    onSubmit={async ({
                        logo: newLogo,
                        participantIntroImage: newParticipantIntroImage,
                    }) => {
                        await updateOrganisation({
                            variables: {
                                organisation: {
                                    logoId: getImageId(logo, newLogo),
                                    participantIntroImageId: getImageId(
                                        participantIntroImage,
                                        newParticipantIntroImage
                                    ),
                                },
                            },
                        });

                        // Redirect to Organisation Colors when organisation has been updated.
                        // Using window.location.href instead of navigate() because the
                        // navigation doesn't trigger a page change when deployed
                        window.location.href = getUrl('ORGANISATION_COLORS');
                    }}
                />
            </CoverPage>
        </>
    );
};
